import {
  nameRegex,
  TYPE_OF_ACCOUNT_CHECK_EXISTS,
} from 'constants/teacherRecruitment';
import { EApiStatus } from 'constants/common';
import {
  EMemberCenterErrorCode,
  RESEND_VERIFICATION_CODE_COOLDOWN_MS,
} from 'constants/memberCenter';
import { EResetPasswordPlatform } from 'types/memberCenter';
import { useNavigate } from 'react-router-dom';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { Mui } from '@onedesign/ui';
import { Icon, SvgName } from 'components/Icon';
import axios from 'plugins/api/axios';
import { useLoadingService } from 'services/LoadingService';
import { PATH } from 'routes/routeConfig';
import { t } from 'utils/i18n';
import { ICheckExistsAccountBody } from 'types/teacherRecruitment';
import {
  StyledContainerMemberInfo,
  StyledFormContainerResetPasswordRequest,
  StyledFormControlMemberConfirm,
  StyledHeaderConfirm,
  StyledHeaderConfirmTitle,
  StyledBackButton,
} from 'pages/Login/Login.style';

const { teacherRecruit, memberCenter } = axios;
const { Button, TextField, FormHelperText } = Mui;

interface IResetPasswordRequestForm {
  accountTeacher: string;
}

const ResetPasswordRequest = () => {
  const schema = yup.object().shape({
    accountTeacher: yup
      .string()
      .transform(value => value.trim())
      .matches(
        nameRegex,
        t(
          'login.resetPasswordRequest.accountFormatInvalid',
          '會員帳號格式錯誤、第一個字限定英文',
        ),
      )
      .min(
        6,
        t(
          'login.resetPasswordRequest.accountTooShort',
          '會員帳號格式錯誤、限定六到三十字元',
        ),
      )
      .max(
        30,
        t(
          'login.resetPasswordRequest.accountTooLong',
          '會員帳號格式錯誤、限定六到三十字元',
        ),
      ),
  });

  const { showLoading, hideLoading } = useLoadingService();
  const navigate = useNavigate();

  const methods = useForm<IResetPasswordRequestForm>({
    mode: 'onSubmit',
    reValidateMode: 'onChange',
    resolver: yupResolver(schema),
    defaultValues: {
      accountTeacher: '',
    },
  });
  const {
    handleSubmit,
    control,
    formState: { errors },
    setError,
  } = methods;

  const checkAccountExists = async (userName: string): Promise<boolean> => {
    const dataCheck = {
      type: TYPE_OF_ACCOUNT_CHECK_EXISTS.USERNAME,
      value: userName,
    } as ICheckExistsAccountBody;
    const {
      status: teacherRecruitResponseStatus,
      data: teacherRecruitData,
      error: teacherRecruitError,
    } = await teacherRecruit.checkExistsAccount(dataCheck);

    return (
      teacherRecruitResponseStatus === EApiStatus.SUCCESS &&
      teacherRecruitData?.exist === true &&
      !teacherRecruitError
    );
  };

  const handleSubmitForm = async ({
    accountTeacher = '',
  }: IResetPasswordRequestForm) => {
    showLoading();

    const isAccountExists = await checkAccountExists(accountTeacher);

    if (!isAccountExists) {
      hideLoading();
      setError('accountTeacher', {
        type: 'manual',
        message: t('login.resetPasswordRequest.invalid', 'Invalid OneClub ID'),
      });
      return;
    }

    const res = await memberCenter.initiateResetPassword({
      account: accountTeacher,
      platform: EResetPasswordPlatform.EMAIL,
    });

    hideLoading();

    if (res.err !== EMemberCenterErrorCode.SUCCESS || !res.data) {
      setError('accountTeacher', {
        type: 'manual',
        message:
          res.msg ||
          t('login.resetPasswordRequest.invalid', 'Invalid OneClub ID'),
      });
      return;
    }

    const resendAvailableAt = Date.now() + RESEND_VERIFICATION_CODE_COOLDOWN_MS;
    const params = new URLSearchParams({
      account: accountTeacher,
      email: res.data.email ?? '',
      resendAvailableAt: String(resendAvailableAt),
    });
    navigate(`${PATH.RESET_PASSWORD}?${params.toString()}`);
  };

  return (
    <StyledContainerMemberInfo>
      <StyledBackButton
        color="inherit"
        startIcon={
          <Icon
            sx={{ width: '18px', height: '18px' }}
            name={SvgName.ChevronLeft}
          />
        }
        onClick={() => navigate(PATH.LOGIN)}
      >
        {t('login.resetPasswordRequest.backToLogin', '回登入')}
      </StyledBackButton>

      <StyledHeaderConfirm>
        <StyledHeaderConfirmTitle>
          {t('login.resetPasswordRequest.title', '重設密碼')}
        </StyledHeaderConfirmTitle>
      </StyledHeaderConfirm>

      <StyledFormContainerResetPasswordRequest>
        <FormProvider {...methods}>
          <form>
            <StyledFormControlMemberConfirm>
              <Controller
                name="accountTeacher"
                control={control}
                render={({ field }) => (
                  <>
                    <TextField
                      {...field}
                      placeholder={t(
                        'login.resetPasswordRequest.placeholder',
                        '教師帳號',
                      )}
                      label={t(
                        'login.resetPasswordRequest.placeholder',
                        '教師帳號',
                      )}
                      fullWidth
                      error={!!errors.accountTeacher}
                    />
                    {errors.accountTeacher && (
                      <FormHelperText
                        sx={{
                          color: theme => theme.palette.error.main,
                        }}
                      >
                        {errors.accountTeacher.message}
                      </FormHelperText>
                    )}
                  </>
                )}
              />
            </StyledFormControlMemberConfirm>

            <Button
              type="submit"
              sx={{ padding: '12px 0', marginTop: '2rem' }}
              variant="contained"
              onClick={handleSubmit(handleSubmitForm)}
            >
              {t('login.resetPasswordRequest.buttonConfirm', '確認')}
            </Button>
          </form>
        </FormProvider>
      </StyledFormContainerResetPasswordRequest>
    </StyledContainerMemberInfo>
  );
};

export default ResetPasswordRequest;
